/**
 * Escalas de Trabalho — quadro de horários dos funcionários.
 * Tipos: FIXO | FLEXIVEL | 12X36 | 6X1 | TURNO
 */
import { FormEvent, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { escalaApi, getErrorMessage } from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import type { EscalaTrabalho } from '../../types/api'

const TIPO_LABELS: Record<string, { label: string; desc: string }> = {
  FIXO:     { label: 'Horário Fixo',   desc: 'Entrada/saída definidos, mesmo horário todo dia' },
  FLEXIVEL: { label: 'Flexível',        desc: 'Carga horária diária sem horário fixo de entrada' },
  '12X36':  { label: '12x36',           desc: '12h de trabalho / 36h de folga — ciclo rotativo' },
  '6X1':    { label: '6x1',             desc: '6 dias de trabalho / 1 dia de folga' },
  TURNO:    { label: 'Turno Rotativo', desc: 'Turnos configuráveis (manhã, tarde, noite)' },
}

const FORM_EMPTY = {
  nome: '', tipo: 'FIXO', entrada: '', saida: '', intervalo_inicio: '', intervalo_fim: '',
  trabalha_seg: true, trabalha_ter: true, trabalha_qua: true,
  trabalha_qui: true, trabalha_sex: true, trabalha_sab: false, trabalha_dom: false,
  carga_horaria_dia: '', config_json: '',
}

const DIAS_LABELS = [
  { key: 'trabalha_seg', label: 'Seg' },
  { key: 'trabalha_ter', label: 'Ter' },
  { key: 'trabalha_qua', label: 'Qua' },
  { key: 'trabalha_qui', label: 'Qui' },
  { key: 'trabalha_sex', label: 'Sex' },
  { key: 'trabalha_sab', label: 'Sáb' },
  { key: 'trabalha_dom', label: 'Dom' },
] as const

type DiasKey = typeof DIAS_LABELS[number]['key']

export function EscalasPage() {
  const qc = useQueryClient()
  const { data: escalas, isLoading } = useQuery({ queryKey: ['escalas'], queryFn: () => escalaApi.list() })

  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState(FORM_EMPTY)
  const [formError, setFormError] = useState<string | null>(null)

  const saveMutation = useMutation({
    mutationFn: () => {
      const tipo = form.tipo as EscalaTrabalho['tipo']
      const payload = {
        ...form,
        tipo,
        entrada: form.entrada || null,
        saida: form.saida || null,
        intervalo_inicio: form.intervalo_inicio || null,
        intervalo_fim: form.intervalo_fim || null,
        carga_horaria_dia: form.carga_horaria_dia ? Number(form.carga_horaria_dia) : null,
        config_json: form.config_json || null,
      }
      return editId ? escalaApi.update(editId, payload) : escalaApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['escalas'] }); closeForm() },
    onError: err => setFormError(getErrorMessage(err)),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => escalaApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['escalas'] }),
  })

  function openCreate() { setEditId(null); setForm(FORM_EMPTY); setFormError(null); setShowForm(true) }
  function openEdit(e: EscalaTrabalho) {
    setEditId(e.id)
    setForm({
      nome: e.nome, tipo: e.tipo, entrada: e.entrada ?? '', saida: e.saida ?? '',
      intervalo_inicio: e.intervalo_inicio ?? '', intervalo_fim: e.intervalo_fim ?? '',
      trabalha_seg: e.trabalha_seg, trabalha_ter: e.trabalha_ter, trabalha_qua: e.trabalha_qua,
      trabalha_qui: e.trabalha_qui, trabalha_sex: e.trabalha_sex, trabalha_sab: e.trabalha_sab,
      trabalha_dom: e.trabalha_dom,
      carga_horaria_dia: e.carga_horaria_dia != null ? String(e.carga_horaria_dia) : '',
      config_json: e.config_json ?? '',
    })
    setFormError(null); setShowForm(true)
  }
  function closeForm() { setShowForm(false); setEditId(null) }

  const dias_trabalhados = (e: EscalaTrabalho) =>
    DIAS_LABELS.filter(d => e[d.key]).map(d => d.label).join(' · ')

  const tipo_info = (tipo: string) => TIPO_LABELS[tipo] ?? { label: tipo, desc: '' }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <div>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>Escalas de Trabalho</h1>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Quadro de horários — entrada/saída, intervalo e dias trabalhados
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Nova Escala</button>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 'var(--space-4)' }}
          onClick={e => { if (e.target === e.currentTarget) closeForm() }}>
          <div className="card" style={{ width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto' }}>
            <h2 style={{ fontWeight: 600, marginBottom: 'var(--space-4)' }}>{editId ? 'Editar Escala' : 'Nova Escala'}</h2>
            {formError && <div className="alert alert-danger" style={{ marginBottom: 'var(--space-4)' }}>{formError}</div>}
            <form onSubmit={(e: FormEvent) => { e.preventDefault(); saveMutation.mutate() }} noValidate>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <F label="Nome da escala">
                    <input className="input" value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Ex: Administrativo 8h, Turno Manhã" required />
                  </F>
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <F label="Tipo de escala">
                    <select className="input" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                      {Object.entries(TIPO_LABELS).map(([k, { label, desc }]) => (
                        <option key={k} value={k}>{label} — {desc}</option>
                      ))}
                    </select>
                  </F>
                </div>
              </div>

              {form.tipo === 'FIXO' && (
                <>
                  <SectionHeader>Horário</SectionHeader>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                    <F label="Entrada">
                      <input className="input" type="time" value={form.entrada} onChange={e => setForm(f => ({ ...f, entrada: e.target.value }))} />
                    </F>
                    <F label="Saída">
                      <input className="input" type="time" value={form.saida} onChange={e => setForm(f => ({ ...f, saida: e.target.value }))} />
                    </F>
                    <F label="Início do intervalo" opt>
                      <input className="input" type="time" value={form.intervalo_inicio} onChange={e => setForm(f => ({ ...f, intervalo_inicio: e.target.value }))} />
                    </F>
                    <F label="Fim do intervalo" opt>
                      <input className="input" type="time" value={form.intervalo_fim} onChange={e => setForm(f => ({ ...f, intervalo_fim: e.target.value }))} />
                    </F>
                  </div>
                </>
              )}

              {form.tipo === 'FLEXIVEL' && (
                <>
                  <SectionHeader>Carga Horária</SectionHeader>
                  <F label="Horas por dia">
                    <input className="input" type="number" min={1} max={12} step={0.5} value={form.carga_horaria_dia} onChange={e => setForm(f => ({ ...f, carga_horaria_dia: e.target.value }))} placeholder="8" />
                  </F>
                </>
              )}

              {(form.tipo === 'TURNO') && (
                <>
                  <SectionHeader>Configuração JSON</SectionHeader>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', marginBottom: 'var(--space-2)' }}>
                    Ex: {'[{"nome":"Manhã","entrada":"06:00","saida":"14:00"},{"nome":"Tarde","entrada":"14:00","saida":"22:00"}]'}
                  </p>
                  <textarea className="input" rows={4} style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }} value={form.config_json} onChange={e => setForm(f => ({ ...f, config_json: e.target.value }))} />
                </>
              )}

              {(form.tipo !== 'TURNO') && (
                <>
                  <SectionHeader>Dias da Semana</SectionHeader>
                  <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
                    {DIAS_LABELS.map(({ key, label }) => (
                      <label key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={form[key as DiasKey] as boolean} onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))} />
                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500, color: (form[key as DiasKey] as boolean) ? 'var(--color-primary)' : 'var(--color-gray-400)' }}>{label}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
                <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Salvando…' : 'Salvar'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={closeForm}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isLoading ? <PageLoading /> : !escalas?.length ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
          <p style={{ color: 'var(--color-gray-400)' }}>Nenhuma escala cadastrada.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 'var(--space-4)' }}>
          {escalas.map((e: EscalaTrabalho) => {
            const ti = tipo_info(e.tipo)
            return (
              <div key={e.id} className="card" style={{ opacity: e.ativo ? 1 : 0.6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-3)' }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{e.nome}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', fontWeight: 600 }}>{ti.label}</div>
                  </div>
                  {!e.ativo && <span className="badge badge-gray">Inativa</span>}
                </div>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-700)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {e.entrada && <div><span style={{ color: 'var(--color-gray-400)' }}>Horário: </span>{e.entrada} – {e.saida}</div>}
                  {e.intervalo_inicio && <div><span style={{ color: 'var(--color-gray-400)' }}>Intervalo: </span>{e.intervalo_inicio} – {e.intervalo_fim}</div>}
                  {e.carga_horaria_dia && <div><span style={{ color: 'var(--color-gray-400)' }}>Carga: </span>{e.carga_horaria_dia}h/dia</div>}
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>{dias_trabalhados(e)}</div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-gray-100)' }}>
                  <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)' }} onClick={() => openEdit(e)}>Editar</button>
                  <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
                    onClick={() => { if (confirm(`Remover "${e.nome}"?`)) deleteMutation.mutate(e.id) }}>Remover</button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--color-gray-500)', borderBottom: '1px solid var(--color-gray-200)', paddingBottom: 'var(--space-1)', marginBottom: 'var(--space-3)', marginTop: 'var(--space-2)' }}>{children}</div>
}

function F({ label, children, opt }: { label: string; children: React.ReactNode; opt?: boolean }) {
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>
        {label}{opt && <span style={{ color: 'var(--color-gray-400)', fontWeight: 400 }}> (opcional)</span>}
      </label>
      {children}
    </div>
  )
}
